import { Component, OnInit, Output, EventEmitter, Input, ViewChild } from '@angular/core';
import { OnyxEvent } from '../../models/event';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { PioneerValidators } from '../../../shared/utils/pioneer.validators';
import { Payment } from '../../models/payment';
import { PaymentStore } from '../../service/payment.store';
import { ConfirmationModalComponent } from '../../../shared/confirmation-modal/confirmation-modal.component';
import { PaymentType } from '../../models/payment-type';
import { tap, finalize } from 'rxjs/operators';
import { ConfirmationModalConfig } from 'app/shared/confirmation-modal/confirmation-modal-config';
import { TranslateService } from '@ngx-translate/core';
import { DecimalPipe, UpperCasePipe } from '@angular/common';
import { Party } from '../../models/party';
import { AuthenticationStore } from 'app/permission/services/authentication.store';
import { Unsubscriber } from './../../../shared/helper/unsubscriber';

@Component({
  selector: 'pioneer-add-prepayment',
  templateUrl: './add-prepayment.component.html'
})
export class AddPrepaymentComponent extends Unsubscriber implements OnInit {

  @Input()
  event: OnyxEvent;

  @Input()
  vendor: Party;

  @Output()
  cancel = new EventEmitter();

  @Output()
  save = new EventEmitter();

  prepaymentForm: FormGroup;

  @ViewChild(ConfirmationModalComponent)
  confirmationModal: ConfirmationModalComponent;

  confirmationModalConfig = new ConfirmationModalConfig();

  get prepaymentAmount(): number {
    return this.prepaymentForm ? this.prepaymentForm.get('prepaymentAmount').value : null;
  }

  get description(): string {
    return this.prepaymentForm ? this.prepaymentForm.get('description').value : null;
  }

  @Input()
  minAmount: number;

  constructor(
    private formBuilder: FormBuilder,
    private paymentStore: PaymentStore,
    private translateService: TranslateService,
    private decimalPipe: DecimalPipe,
    private uppercasePipe: UpperCasePipe,
    private authenticationStore: AuthenticationStore
    ) {
      super();
     }

  ngOnInit() {
    if (!this.event) {
      throw new Error('The "event" attribute must be specified to use the ReservationButtonBarComponent component');
    }
    if (!this.vendor) {
      throw new Error('The "vendor" attribute must be specified');
    }
    if (!this.minAmount) {
      throw new Error('The "PREPAYMENT_MIN_AMOUNT" configuration parameter must be set');
    }
    this.initForm();
  }

  cancelClick() {
    this.resetForm();
    this.cancel.emit();
  }

  disableSave() {
    let disable: boolean;
    if (this.prepaymentForm) {
      disable = !this.prepaymentForm.valid;
    } else {
      disable = true;
    }
    return disable;
  }

  saveClick() {
    this.confirmationModalConfig.title = this.translateService.instant(
      'EVENT.PREPAYMENT.PREPAYMENT_CONFIRMATION_TITLE',
      {
        amount: this.decimalPipe.transform(this.prepaymentAmount, '1.2-2'),
        currency: this.uppercasePipe.transform(this.event.currency)
      });
    this.confirmationModal.open();
  }

  prepaymentConfirmationClosed(value): void {
    if (value) {
      this.savePrepayment();
    }
  }

  private initForm() {
    this.prepaymentForm = this.formBuilder.group({
      'prepaymentAmount': [
        null,
        [Validators.required, PioneerValidators.isValidAmountInRange(this.minAmount), Validators.maxLength(11)]
      ],
      'description': [null, [Validators.maxLength(255)]]
    });
  }

  private savePrepayment() {
    this.prepaymentForm.disable();

    const prepayment = new Payment({
      commissionAmount: this.prepaymentAmount,
      commissionCurrency: this.event.currency,
      description: this.description,
      eventId: this.event.eventId,
      type: PaymentType.PREPAYMENT,
      eventPartyId: this.vendor.eventPartyId
    });

    this.paymentStore.addPayment(prepayment).pipe(
      tap(() => {
        if (this.isMultiVendor()) {
          this.paymentStore.loadPayments(this.event.eventId).subscribe();
          this.paymentStore.loadLatestPrepayment(this.event.eventId).subscribe();
        } else {
          this.paymentStore.loadPaymentsByParty(this.event.eventId, this.vendor.eventPartyId).subscribe();
          this.paymentStore.loadLatestPrepaymentByParty(this.event.eventId, this.vendor.eventPartyId).subscribe();
        }
        this.save.emit();
      }),
      finalize(() => {
        this.prepaymentForm.enable();
      })
    ).subscribe();
  }

  private resetForm(): void {
    this.prepaymentForm.reset({
      amount: { value: null, disabled: false },
      description: { value: null, disabled: false }
    });
  }

  isMultiVendor() {
    return this.authenticationStore.hasPermission('MultiVendor');
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/payment/add-prepayment/add-prepayment.component.ts